import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Button, Typography } from '@popmenu/common-ui';
import { useSnackbar } from '~/utils/withSnackbar';

import { Link } from 'react-router-dom';
import phoneUsedByMfa from '~/libs/gql/queries/users/phoneUsedByMfaQuery.gql';
import { useLazyQuery } from '~/lazy_apollo/client';
import { useIntl } from 'react-intl';
import { withIntl } from '../../utils/withIntl';
import { notBlankValidator, passwordValidator, samePasswordValidator, phoneValidator } from '../../utils/forms';
import createUserMutation from '../../libs/gql/mutations/users/createUserMutation.gql';
import { withStyles } from '../../utils/withStyles';
import signInStyles from '../../assets/jss/shared/signInStyles';

import BasicForm, { SubmitGroup, TextFieldGroup } from '../shared/forms/BasicForm';
import SocialControls from '../../shared/sessions/SocialControls';
import Grid from '../../shared/Grid';

const useMessages = () => {
  const intl = useIntl();
  return {
    numberIsAlreadyUsed: intl.formatMessage({ defaultMessage: 'Number is already linked to another user' }),
    phoneUsedCheckFailed: intl.formatMessage({ defaultMessage: 'Phone used check failed' }),
  };
};

const SignUpForm = ({
  afterSignUp = null,
  classes,
  email = null,
  facebookOauthDisabled = false,
  onSignUpError = null,
  onSignUpStart = null,
  onUserExists = null,
  readOnlyEmail = false,
  showAllFields = true,
  style = null,
  t,
  variant = 'standard',
}) => {
  const searchParams = new URLSearchParams(document.location.search);
  const isOauth = searchParams.get('oauth') === '1';
  const { showSnackbarError } = useSnackbar();
  const messages = useMessages();
  const [phoneValue, setPhoneValue] = useState('');
  const [checkPhone, { data, loading }] = useLazyQuery(phoneUsedByMfa, { fetchPolicy: 'network-only' });
  const formApiRef = useRef(null);
  // simple cache: remembers the last phone checked and its result
  const lastCheckedRef = useRef({ phone: null, used: null });
  const reqCounter = useRef(0);
  useEffect(() => {
    if (!lastCheckedRef.current.used) return;
    if (lastCheckedRef.current.phone !== phoneValue) {
      lastCheckedRef.current = { phone: phoneValue, used: false };
      formApiRef.current?.setFieldMeta?.('phone', { error: false });
    }
  }, [phoneValue]);
  const combinedValidator = useCallback((value, instance) => {
    if (lastCheckedRef.current && lastCheckedRef.current.used) {
      return messages.numberIsAlreadyUsed;
    }
    const instanceError = instance && instance.meta && instance.meta.error;
    if (instanceError) return instanceError;
    return phoneValidator(value, instance);
  }, [messages.numberIsAlreadyUsed]);
  const handlePhoneBlur = async (e) => {
    const value = (e?.target?.value ?? '').trim();
    if (!value) return;
    // use simple cache to avoid repeated calls for the same value
    if (lastCheckedRef.current.phone === value) {
      return;
    }
    // increment request counter to ignore stale responses later
    reqCounter.current += 1;
    const thisReq = reqCounter.current;
    try {
      const result = await checkPhone({
        variables: { phone: value },
      });
      // If an error exists, it throws it.
      if ((result?.errors && result.errors.length > 0) || !result?.data) {
        throw new Error(messages.phoneUsedCheckFailed);
      }
      // if another request was triggered in the meantime, we ignore this response
      if (thisReq !== reqCounter.current) return;
      const used = Boolean(result?.data?.phoneUsedByMfa);
      lastCheckedRef.current = { phone: value, used };
      formApiRef.current?.setFieldMeta('phone', { error: used ? messages.numberIsAlreadyUsed : false });
    } catch (err) {
      showSnackbarError(messages.phoneUsedCheckFailed);
    }
  };
  const handlePhoneChange = useCallback((value) => {
    setPhoneValue(value);
    if (lastCheckedRef.current && lastCheckedRef.current.used && lastCheckedRef.current.phone !== value) {
      lastCheckedRef.current = { phone: value, used: false };
      formApiRef.current?.setFieldMeta?.('phone', { error: false });
    }
  }, []);
  return (
    <BasicForm
      defaultValues={{ email }}
      labelPosition="top"
      onSubmit={() => {
        if (typeof onSignUpStart === 'function') {
          onSignUpStart();
        }
      }}
      mutate={{
        mutation: createUserMutation,
        onCompleted: () => {
          if (typeof afterSignUp === 'function') {
            afterSignUp();
          }
        },
        onError: (error) => {
          if (typeof onSignUpError === 'function') {
            onSignUpError(error);
          }
          if (error.message === 'sign_up.email_taken') {
            if (typeof onUserExists === 'function') {
              onUserExists();
            }
          }
        },
        toVariables: ({ facebookCode, googleCode, ...variables }) => ({
          createAccount: isOauth,
          facebookCode,
          googleCode,
          userInput: {
            ...variables,
            name: undefined,
          },
        }),
      }}
    >
      {(formApi) => {
        formApiRef.current = formApi;
        const { values, submitManual } = formApi;
        const isFormIncomplete = isOauth ? (!values.email || !values.password || !values.passwordConfirmation) : (!values.email || !values.password);
        const isPhoneUsedByMfa = loading || !!data?.phoneUsedByMfa;
        const submitDisabled = isFormIncomplete || isPhoneUsedByMfa;
        return (
          <React.Fragment>
            {isOauth && (
              <React.Fragment>
                <Grid container spacing={2}>
                  <Grid xs={6} item>
                    <TextFieldGroup
                      field="firstName"
                      placeholder={t('users.first_name')}
                      style={style}
                      validate={notBlankValidator}
                      variant={variant}
                    />
                  </Grid>
                  <Grid xs={6} item>
                    <TextFieldGroup
                      field="lastName"
                      placeholder={t('users.last_name')}
                      style={style}
                      validate={notBlankValidator}
                      variant={variant}
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <TextFieldGroup
                      required
                      field="email"
                      style={style}
                      placeholder={t('users.email')}
                      type="email"
                      validate={notBlankValidator}
                      variant={variant}
                      disabled={readOnlyEmail}
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <TextFieldGroup
                      required
                      field="phone"
                      style={style}
                      placeholder={t('users.phone')}
                      type="tel"
                      variant={variant}
                      validate={combinedValidator}
                      onBlur={handlePhoneBlur}
                      onTextChange={handlePhoneChange}
                    />
                  </Grid>
                  <Grid xs={6} item>
                    <TextFieldGroup
                      required
                      field="password"
                      label="Password"
                      placeholder={t('users.password')}
                      type="password"
                      validate={passwordValidator}
                      variant={variant}
                    />
                  </Grid>
                  <Grid xs={6} item>
                    <TextFieldGroup
                      required
                      field="passwordConfirmation"
                      label="Confirm Password"
                      placeholder={t('users.password')}
                      type="password"
                      validate={samePasswordValidator}
                      variant={variant}
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <Typography variant="subtitle1">
                      {t('models.user.password_helper_text')}
                    </Typography>
                  </Grid>
                </Grid>
              </React.Fragment>
            )}
            {
              !isOauth && (
                <Grid container spacing={2}>
                  <Grid xs={12} item>
                    <TextFieldGroup
                      required
                      field="email"
                      style={style}
                      placeholder={t('users.email')}
                      type="email"
                      validate={notBlankValidator}
                      variant={variant}
                      disabled={readOnlyEmail}
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <TextFieldGroup
                      required
                      field="password"
                      helperText={t('models.user.password_helper_text')}
                      label="Password"
                      placeholder={t('users.password')}
                      type="password"
                      validate={passwordValidator}
                      variant={variant}
                      data-tour-id="sign-up-form-password"
                    />
                  </Grid>
                </Grid>
              )
            }
            {showAllFields && (
              <React.Fragment>
                <TextFieldGroup
                  field="firstName"
                  placeholder={t('users.first_name')}
                  style={style}
                  validate={notBlankValidator}
                  variant={variant}
                />
                <TextFieldGroup
                  field="lastName"
                  placeholder={t('users.last_name')}
                  style={style}
                  validate={notBlankValidator}
                  variant={variant}
                />
                <TextFieldGroup
                  field="phone"
                  placeholder={t('users.phone')}
                  style={style}
                  type="tel"
                  variant={variant}
                  validate={combinedValidator}
                  onBlur={handlePhoneBlur}
                  onTextChange={handlePhoneChange}
                />
              </React.Fragment>
            )}
            <Typography
              align="center"
              className={classes.disclaimer}
              variant="body2"
            >
              <FormattedMessage
                defaultMessage="By clicking the 'Sign Up' button, you agree to Popmenu&apos;s {termsLink} and {privacyLink}."
                values={{
                  privacyLink: (
                    <Button
                      className={classes.termsButton}
                      component={Link}
                      size="small"
                      target="_blank"
                      to="/privacy"
                      variant="text"
                    >
                      <FormattedMessage defaultMessage="Privacy Policy" />
                    </Button>
                  ),
                  termsLink: (
                    <Button
                      className={classes.termsButton}
                      component={Link}
                      size="small"
                      target="_blank"
                      to="/terms"
                      variant="text"
                    >
                      <FormattedMessage defaultMessage="Terms" />
                    </Button>
                  ),
                }}
              />
            </Typography>
            <SubmitGroup
              color="secondary"
              disabled={submitDisabled}
              data-cy="sign_up_submit"
              icon={false}
              justifyContent="center"
              size="lg"
              title={t('sessions.sign_up')}
            />

            {isOauth ? (
              <div className={classes.socialSeparatorContainer}>
                <div className={classes.socialSeparator} aria-hidden="true">
                  <div className={classes.socialSeparatorLine} />
                </div>
                <Typography align="center" className={classes.socialSeparatorText}>
                  <FormattedMessage defaultMessage="or" />
                </Typography>
              </div>
            ) :
              (
                <React.Fragment>
                  <Typography align="center" className={classes.socialSeparatorOld}>
                    <FormattedMessage defaultMessage="or" />
                  </Typography>
                  <SocialControls
                    facebookOauthDisabled={facebookOauthDisabled}
                    submit={(_e, variables) => submitManual(variables)}
                  />
                </React.Fragment>
              )}
          </React.Fragment>
        );
      }}
    </BasicForm>
  );
};

SignUpForm.propTypes = {
  afterSignUp: PropTypes.func,
  classes: PropTypes.object.isRequired,
  email: PropTypes.string,
  facebookOauthDisabled: PropTypes.bool,
  onSignUpError: PropTypes.func,
  onSignUpStart: PropTypes.func,
  onUserExists: PropTypes.func,
  readOnlyEmail: PropTypes.bool,
  showAllFields: PropTypes.bool,
  style: PropTypes.object,
  t: PropTypes.func.isRequired,
  variant: PropTypes.string,
};

export default withIntl(withStyles(signInStyles)(SignUpForm));
